import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import FavoriteButton from "@/components/feature/FavoriteButton";
import RelatedMiniCard from "@/components/feature/RelatedMiniCard";
import { allMovies, getMovieById } from "@/utils/catalog";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export default function MovieDetailPage() {
  const { id } = useParams();
  const movie = getMovieById(id || "");

  useDocumentTitle(
    movie ? `${movie.title} - 影视详情 - 17C.COM` : "影视详情 - 17C.COM",
    movie ? movie.summary : undefined
  );

  if (!movie) {
    return (
      <div className="w-full px-4 md:px-8 py-16">
        <EmptyState
          icon="ri-emotion-sad-line"
          title="影视内容不存在或已下架"
          description="你访问的影视内容可能已被移除，返回影视分类页看看其他内容吧。"
          actionLabel="返回影视分类"
          onAction={() => window.REACT_APP_NAVIGATE("/category/movie")}
        />
      </div>
    );
  }

  const related = allMovies
    .filter(
      (m) =>
        m.id !== movie.id &&
        (m.genre === movie.genre || m.tags.some((t) => movie.tags.includes(t)))
    )
    .slice(0, 5);

  const infoRows = [
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.cast.join(" / ") },
    { label: "类型", value: movie.genre },
    { label: "地区", value: movie.region },
    { label: "上映日期", value: movie.releaseDate },
    { label: "片长", value: movie.duration },
  ];

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-8 pt-6 pb-2">
        <Breadcrumb
          items={[{ label: "首页", to: "/" }, { label: "影视", to: "/category/movie" }, { label: movie.title }]}
        />
      </div>

      <div className="w-full px-4 md:px-8 pt-4 pb-10 md:pb-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
              <div className="flex flex-col sm:flex-row gap-5 md:gap-7">
                <div className="w-full sm:w-56 shrink-0">
                  <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
                    <img
                      src={movie.cover}
                      alt={movie.title}
                      title={`${movie.title} - 影视海报`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-label font-medium bg-primary-100 text-primary-700">
                    {movie.genre}
                  </span>
                  <h1 className="mt-3 font-heading text-2xl md:text-3xl font-black text-foreground-950">
                    {movie.title}
                  </h1>
                  <p className="mt-1 text-sm text-foreground-500">{movie.subtitle}</p>

                  <div className="mt-4 flex items-center gap-4 flex-wrap text-sm">
                    <span className="flex items-center gap-1 text-accent-700">
                      <i className="ri-fire-line"></i>
                      热度 {movie.heat}
                    </span>
                    <span className="text-foreground-500">{movie.year} 年</span>
                    <span className="text-foreground-500">{movie.region}</span>
                  </div>

                  <div className="mt-5 flex flex-wrap gap-2">
                    {movie.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2.5 py-1 rounded-md text-xs bg-secondary-100 text-secondary-800"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="mt-6 flex flex-wrap items-center gap-3">
                    <FavoriteButton kind="movie" id={movie.id} />
                    <Link
                      to="/category/movie"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-100 text-foreground-800 border border-background-300 text-sm font-medium hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-arrow-go-back-line"></i>
                      更多影视
                    </Link>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-background-200">
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                  {infoRows.map((row) => (
                    <div key={row.label} className="flex gap-3 text-sm">
                      <dt className="w-20 shrink-0 text-foreground-500">{row.label}</dt>
                      <dd className="text-foreground-900">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
              <h2 className="font-heading text-xl font-black text-foreground-950 mb-4 flex items-center gap-2">
                <span className="w-1.5 h-5 rounded-full bg-accent-500"></span>
                剧情简介
              </h2>
              <p className="text-sm md:text-base leading-relaxed text-foreground-700 whitespace-pre-line">
                {movie.synopsis}
              </p>
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-4 md:p-5 flex flex-wrap items-center gap-3 text-sm">
              <span className="text-foreground-600">所属分类：</span>
              <Link
                to="/category/movie"
                className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 cursor-pointer"
              >
                <i className="ri-film-line"></i>
                影视
              </Link>
              <span className="inline-flex items-center gap-1 text-foreground-600">
                <i className="ri-price-tag-3-line"></i>
                {movie.genre}
              </span>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h2 className="font-heading text-lg font-black text-foreground-950 mb-2 flex items-center gap-2">
                <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
                相关推荐
              </h2>
              {related.length > 0 ? (
                <div className="flex flex-col">
                  {related.map((item) => (
                    <RelatedMiniCard
                      key={item.id}
                      to={`/detail/movie/${item.id}`}
                      cover={item.cover}
                      title={item.title}
                      meta={`${item.region} · ${item.year} · ${item.genre}`}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-foreground-500 py-4">暂无相关推荐内容</p>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}